import { useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Copy, Inbox, Info, Lock, RefreshCw, WifiOff, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/http';
import { Button } from './Button';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton rounded', className)} aria-hidden />;
}

/** Several skeleton lines for a loading block; announces "Đang tải" to screen readers. */
export function SkeletonBlock({ lines = 4, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2.5', className)} role="status" aria-label="Đang tải">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-4', i % 3 === 2 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

export function Spinner({ className, label = 'Đang tải' }: { className?: string; label?: string }) {
  return (
    <span role="status" aria-label={label} className={cn('inline-block size-4 animate-spin rounded-full border-2 border-line-strong border-t-accent', className)} />
  );
}

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, action, icon, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center', className)}>
      <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-surface-2 text-subtle [&_svg]:size-5">
        {icon ?? <Inbox aria-hidden />}
      </div>
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="max-w-md text-[13px] text-muted">{description}</p>}
      {action && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}

export function CopyButton({ value, label = 'Sao chép', className }: { value: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={cn('inline-flex items-center gap-1 rounded px-1 text-xs text-muted hover:bg-surface-2 hover:text-ink', className)}
      aria-label={`${label}: ${value}`}
      title={label}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(
          () => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1500);
          },
          () => undefined,
        );
      }}
    >
      {done ? <Check className="size-3.5 text-success" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      <span className="sr-only">{done ? 'Đã sao chép' : label}</span>
    </button>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
  compact?: boolean;
}

/** Error block with Vietnamese message, and, for server failures only, a copyable support trace id. */
export function ErrorState({ error, onRetry, title, className, compact }: ErrorStateProps) {
  const api = error instanceof ApiError ? error : null;
  if (api?.isForbidden) return <PermissionDenied className={className} />;
  const network = api?.isNetwork;
  const Icon = network ? WifiOff : XCircle;
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-2 text-center',
        compact ? 'px-4 py-6' : 'px-6 py-12',
        className,
      )}
    >
      <Icon className="size-6 text-danger" aria-hidden />
      <p className="text-sm font-medium text-ink">{title ?? (network ? 'Mất kết nối máy chủ' : 'Không tải được dữ liệu')}</p>
      <p className="break-anywhere max-w-lg text-[13px] text-muted">
        {api?.detail ?? (error instanceof Error ? error.message : 'Đã có lỗi xảy ra.')}
      </p>
      {api?.supportTraceId && (
        <p className="break-anywhere flex flex-wrap items-center justify-center gap-x-3 font-mono text-2xs text-subtle">
          <span className="inline-flex items-center">
            Mã hỗ trợ: {api.supportTraceId}
            <CopyButton value={api.supportTraceId} label="Sao chép mã hỗ trợ" />
          </span>
        </p>
      )}
      {onRetry && (
        <Button size="sm" className="mt-2" onClick={onRetry} icon={<RefreshCw className="size-3.5" aria-hidden />}>
          Thử lại
        </Button>
      )}
    </div>
  );
}

export function PermissionDenied({ className, message }: { className?: string; message?: ReactNode }) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-12 text-center', className)} role="alert">
      <Lock className="size-6 text-subtle" aria-hidden />
      <p className="text-sm font-medium text-ink">Bạn không có quyền truy cập mục này</p>
      <p className="max-w-md text-[13px] text-muted">
        {message ?? 'Liên hệ quản trị hệ thống nếu bạn cần được cấp quyền. Quyền được kiểm tra lại ở máy chủ cho mọi thao tác.'}
      </p>
    </div>
  );
}

export type BannerTone = 'info' | 'warning' | 'danger' | 'success' | 'neutral';

const bannerTone: Record<BannerTone, string> = {
  info: 'border-info/30 bg-info-soft text-info',
  warning: 'border-warning/30 bg-warning-soft text-warning',
  danger: 'border-danger/30 bg-danger-soft text-danger',
  success: 'border-success/30 bg-success-soft text-success',
  neutral: 'border-line bg-surface-2 text-muted',
};

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  className,
  icon,
}: {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  icon?: ReactNode;
}) {
  const Icon = tone === 'warning' || tone === 'danger' ? AlertTriangle : Info;
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex flex-wrap items-start gap-x-3 gap-y-2.5 rounded-md border px-4 py-3 text-[13px] leading-normal', bannerTone[tone], className)}
    >
      <span className="mt-0.5 shrink-0">{icon ?? <Icon className="size-4" aria-hidden />}</span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(!!title && 'mt-1', 'text-ink/80')}>{children}</div>}
      </div>
      {action && <div className="ml-7 shrink-0 sm:ml-0">{action}</div>}
    </div>
  );
}

/** 409 VERSION_CONFLICT: someone else changed the record. Offer reload; never silently overwrite. */
export function ConflictBanner({ onReload, className, message }: { onReload: () => void; className?: string; message?: ReactNode }) {
  return (
    <Banner
      tone="warning"
      className={className}
      title="Dữ liệu đã thay đổi"
      action={
        <Button size="sm" onClick={onReload} icon={<RefreshCw className="size-3.5" aria-hidden />}>
          Tải lại
        </Button>
      }
    >
      {message ?? 'Bản ghi vừa được người khác cập nhật. Tải lại để xem phiên bản mới nhất trước khi sửa tiếp — thay đổi chưa lưu của bạn sẽ không được ghi đè.'}
    </Banner>
  );
}

/** Read-only notice for submitted/approved/terminal records. */
export function ReadOnlyNotice({ children }: { children: ReactNode }) {
  return (
    <Banner tone="neutral" icon={<Lock className="size-4" aria-hidden />}>
      {children}
    </Banner>
  );
}
